// =============================================
// TradingViewChart.jsx - 캔들스틱 차트 컴포넌트
// =============================================
// TradingView에서 만든 오픈소스 차트 라이브러리인
// "lightweight-charts"를 사용해서 차트를 그려줘.
//
// 이 컴포넌트를 사용하는 방법:
//   <TradingViewChart data={candles} height={400} />
//
// data 형식:
//   [{time: 1234567890, open: 100, high: 110, low: 90, close: 105}, ...]
// =============================================

import React, { useEffect, useRef } from 'react';
import { createChart, CandlestickSeries, HistogramSeries } from 'lightweight-charts';

function TradingViewChart({ data = [], height = 400, currency = 'KRW' }) {
  // useRef: React가 관리하는 DOM 요소에 접근할 때 사용해
  // 차트가 그려질 <div> 요소를 가리켜
  const chartContainerRef = useRef(null);
  // 차트 인스턴스를 저장해두는 ref
  const chartRef = useRef(null);

  useEffect(() => {
    // 데이터가 없거나 컨테이너가 없으면 아무것도 하지 않아
    if (!chartContainerRef.current || data.length === 0) return;

    // =============================================
    // 1. 차트 생성
    // =============================================
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: height,
      layout: {
        background: { color: '#1a202c' },   // 배경색 (어두운 색)
        textColor: '#d1d5db',               // 글자색
      },
      grid: {
        vertLines: { color: '#2d3748' },    // 세로 격자선 색
        horzLines: { color: '#2d3748' },    // 가로 격자선 색
      },
      crosshair: {
        // 마우스를 올렸을 때 나타나는 십자선
        mode: 1,
      },
      rightPriceScale: {
        borderColor: '#4a5568',             // 오른쪽 가격축 테두리
      },
      timeScale: {
        borderColor: '#4a5568',             // 시간축 테두리
        timeVisible: true,                  // 시간 표시
        secondsVisible: false,              // 초 단위는 숨기기
      },
    });

    // =============================================
    // 2. 캔들스틱(봉차트) 시리즈 추가 - v5 API
    // =============================================
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a',       // 상승봉 색 (초록)
      downColor: '#ef5350',     // 하락봉 색 (빨강)
      borderVisible: false,
      wickUpColor: '#26a69a',   // 위 꼬리 색
      wickDownColor: '#ef5350', // 아래 꼬리 색
    });

    // =============================================
    // 3. 거래량 시리즈 추가 (차트 아래 막대)
    // =============================================
    const volumeSeries = chart.addSeries(HistogramSeries, {
      color: '#4a5568',
      priceFormat: { type: 'volume' },
      // 메인 차트의 20% 높이를 차지하게 설정
      priceScaleId: 'volume',
    });
    chart.priceScale('volume').applyOptions({
      scaleMargins: {
        top: 0.8,   // 위에서 80% 지점부터 시작 (즉, 아래 20%만 사용)
        bottom: 0,
      },
    });

    // =============================================
    // 4. 데이터 적용
    // =============================================
    // 시간 순서로 정렬 (오래된 것부터)
    const sorted = [...data].sort((a, b) => a.time - b.time);

    candleSeries.setData(sorted);

    // 거래량 데이터 (있는 경우만)
    if (sorted[0]?.volume !== undefined) {
      const volumeData = sorted.map(d => ({
        time: d.time,
        value: d.volume,
        // 상승이면 초록, 하락이면 빨강
        color: d.close >= d.open ? '#26a69a44' : '#ef535044',
      }));
      volumeSeries.setData(volumeData);
    }

    // 차트를 최신 데이터에 맞춰 자동으로 맞춰줘
    chart.timeScale().fitContent();

    // =============================================
    // 5. 창 크기가 바뀔 때 차트 크기도 같이 바꿔
    // =============================================
    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', handleResize);

    // 차트 인스턴스 저장
    chartRef.current = chart;

    // =============================================
    // 6. 정리(cleanup) 함수
    // =============================================
    // 이 컴포넌트가 화면에서 사라질 때 차트도 제거해줘야 해.
    // 안 하면 메모리 낭비가 생겨!
    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
      chartRef.current = null;
    };
  }, [data, height]); // data나 height가 바뀌면 차트를 다시 그려

  return (
    <div>
      {/* 차트가 그려질 공간 */}
      <div
        ref={chartContainerRef}
        style={{ width: '100%', borderRadius: '8px', overflow: 'hidden' }}
      />
      {/* 데이터가 없을 때 안내 문구 */}
      {data.length === 0 && (
        <div style={{
          height: `${height}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#1a202c',
          color: '#4a5568',
          borderRadius: '8px',
          fontSize: '0.9rem',
        }}>
          차트 데이터를 불러오는 중이거나 데이터가 없어
        </div>
      )}
    </div>
  );
}

export default TradingViewChart;
